import { useState } from "react";
import {
  ArrowLeft,
  Home,
  BarChart3,
  Calendar,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  Eye,
  Lock,
} from "lucide-react";

const AdminDashboard = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const ADMIN_PASSWORD = "GioAntonio785!!8";

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
    }
  };

  // Mock data for dashboard
  const properties = [
    {
      id: 1,
      name: "Woorim Beach House",
      location: "Woorim, Bribie Island",
      occupancy: 78,
      revenue: 4250,
      rating: 4.9,
      reviews: 24,
    },
    {
      id: 2,
      name: "Bongaree Coastal Retreat",
      location: "Bongaree, Bribie Island",
      occupancy: 71,
      revenue: 3800,
      rating: 5.0,
      reviews: 18,
    },
  ];

  const upcomingBookings = [
    {
      id: 1,
      property: "Woorim Beach House",
      guest: "Sarah M.",
      dates: "Dec 15-20, 2025",
      status: "Confirmed",
    },
    {
      id: 2,
      property: "Bongaree Coastal Retreat",
      guest: "Michael T.",
      dates: "Dec 18-22, 2025",
      status: "Confirmed",
    },
    {
      id: 3,
      property: "Woorim Beach House",
      guest: "Emma L.",
      dates: "Dec 22-28, 2025",
      status: "Pending",
    },
  ];

  const recentReviews = [
    {
      id: 1,
      property: "Woorim Beach House",
      guest: "John D.",
      rating: 5,
      comment: "Amazing stay! The property was spotless and Kellie was incredibly helpful.",
      date: "Dec 5, 2025",
    },
    {
      id: 2,
      property: "Bongaree Coastal Retreat",
      guest: "Lisa K.",
      rating: 5,
      comment: "Perfect location and beautifully presented. Will definitely return!",
      date: "Dec 2, 2025",
    },
  ];

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-bark-light hover:text-bark mb-8 transition-colors"
            data-testid="admin-back-button"
          >
            <ArrowLeft size={20} />
            Back to Website
          </button>

          {/* Login Card */}
          <div className="bg-white p-8 border border-bark/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gold-light text-gold flex items-center justify-center mx-auto mb-4">
                <Lock size={32} />
              </div>
              <h1 className="text-2xl font-serif text-bark mb-2">Host Login</h1>
              <p className="text-bark-light text-sm">
                Enter your password to access the dashboard
              </p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="Enter password"
                  data-testid="admin-password-input"
                />
                {error && (
                  <p className="text-coral text-sm mt-2" data-testid="admin-error-message">
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                data-testid="admin-login-button"
              >
                Log In
              </button>
            </form>
          </div>

          <p className="text-center text-bark-light text-xs mt-6">
            Contact support@staycraftstudio.com if you need access
          </p>
        </div>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-bark/10 sticky top-0 z-10">
        <div className="container-custom py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-bark-light hover:text-bark transition-colors"
              data-testid="dashboard-back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-serif text-xl text-gold">StayCraft Studio</h1>
            <span className="text-xs bg-gold-light text-gold px-2 py-1 uppercase tracking-wider">
              Dashboard
            </span>
          </div>
          <p className="text-sm text-bark-light">
            Welcome, Kellie
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-8">
        {/* Overview Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-metrics-container">
          <div className="admin-card">
            <div className="flex items-center gap-3 mb-2">
              <Home size={20} className="text-gold" />
              <span className="text-xs uppercase tracking-wider text-bark-light">
                Properties
              </span>
            </div>
            <p className="admin-metric">2</p>
          </div>

          <div className="admin-card">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} className="text-teal" />
              <span className="text-xs uppercase tracking-wider text-bark-light">
                Avg Occupancy
              </span>
            </div>
            <p className="admin-metric text-teal">74%</p>
          </div>

          <div className="admin-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={20} className="text-purple" />
              <span className="text-xs uppercase tracking-wider text-bark-light">
                Monthly Revenue
              </span>
            </div>
            <p className="admin-metric text-purple">$8,050</p>
          </div>

          <div className="admin-card">
            <div className="flex items-center gap-3 mb-2">
              <Star size={20} className="text-coral" />
              <span className="text-xs uppercase tracking-wider text-bark-light">
                Avg Rating
              </span>
            </div>
            <p className="admin-metric text-coral">4.95</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Properties */}
          <div className="lg:col-span-2">
            <div className="admin-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-serif text-bark">Managed Properties</h2>
                <BarChart3 size={20} className="text-bark-light" />
              </div>

              <div className="space-y-4">
                {properties.map((property) => (
                  <div
                    key={property.id}
                    className="p-4 bg-cream border border-bark/5"
                    data-testid={`property-card-${property.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-bark">{property.name}</h3>
                        <p className="text-sm text-bark-light">{property.location}</p>
                      </div>
                      <div className="flex items-center gap-1 text-gold">
                        <Star size={14} className="fill-gold" />
                        <span className="text-sm font-semibold">{property.rating}</span>
                        <span className="text-xs text-bark-light">({property.reviews})</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-bark-light">Occupancy</span>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-2 bg-bark/10">
                            <div
                              className="h-full bg-teal"
                              style={{ width: `${property.occupancy}%` }}
                            />
                          </div>
                          <span className="font-semibold text-bark">{property.occupancy}%</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-bark-light">Monthly Revenue</span>
                        <p className="font-semibold text-bark mt-1">
                          ${property.revenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Bookings */}
            <div className="admin-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-serif text-bark">Upcoming</h2>
                <Calendar size={20} className="text-bark-light" />
              </div>

              <div className="space-y-3">
                {upcomingBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-3 bg-cream border border-bark/5 text-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-bark">{booking.guest}</p>
                        <p className="text-xs text-bark-light">{booking.property}</p>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 ${
                          booking.status === "Confirmed"
                            ? "bg-teal-light text-teal"
                            : "bg-gold-light text-gold"
                        }`}
                      >
                        {booking.status}
                      </span>
                    </div>
                    <p className="text-bark-light mt-2 flex items-center gap-1">
                      <Calendar size={12} />
                      {booking.dates}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Reviews */}
            <div className="admin-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-serif text-bark">Recent Reviews</h2>
                <Eye size={20} className="text-bark-light" />
              </div>

              <div className="space-y-3">
                {recentReviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-3 bg-cream border border-bark/5 text-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <p className="font-semibold text-bark">{review.guest}</p>
                      <div className="flex gap-0.5">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} size={12} className="fill-gold text-gold" />
                        ))}
                      </div>
                    </div>
                    <p className="text-bark-light text-xs italic mb-2">
                      "{review.comment}"
                    </p>
                    <p className="text-xs text-bark-light">{review.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
